import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { onboardingAgentExternalService } from "app/components/onboarding-agent/onboardingAgentExternalService";
import { QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX } from "app/components/React/QuickSearch/config";
import { QuickSearchBotStrip } from "app/components/React/QuickSearch/QuickSearchBotStrip/QuickSearchBotStrip";
import { SearchAutocompleteWithTabs } from "app/components/React/SearchAutocompleteWithTabs/SearchAutocompleteWithTabs";
import { SEARCH_TITLE } from "app/pages/activation/components/HomeSearch/texts";
import type { OnSearchItemClick } from "app/pages/activation/components/HomeSearch/types";
import { SectionTitle } from "app/pages/activation/components/HomeWithData/SectionTitle";
import { useClickOutside } from "app/pages/activation/hooks/useClickOutside";
import { useToggle } from "app/pages/activation/hooks/useToggle";
import { activationTracking } from "app/pages/activation/services/tracking";

import { searchBarDataAutomation } from "../dataAutomationKeys";

import * as SC from "./styles";

export const SearchBar = () => {
    const { translate } = useTranslate();
    const [isOpen, open, close] = useToggle();
    const searchRef = useRef<HTMLDivElement>(null);
    const track = activationTracking.homePageWithData;
    const isFirstMountRef = useRef(true);

    const [searchQuery, setSearchQuery] = useState("");
    const [isBotAvailable, setIsBotAvailable] = useState(false);

    useClickOutside(searchRef, close, isOpen);

    useEffect(() => {
        const el = searchRef.current;
        if (!isOpen || !el) {
            return;
        }

        const handleWheel = (e: WheelEvent) => {
            e.stopPropagation();
            // preventDefault() blocks the browser's native page scroll.
            // Safe to call unconditionally here because ScrollArea (react-scrollbar)
            // manages its position via JS state/transforms — not native CSS overflow —
            // so preventDefault() does not interfere with scrolling through results.
            e.preventDefault();
        };

        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [isOpen]);

    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }

        if (isOpen) {
            track.trackSearchBarOpen(onboardingAgentExternalService.isRegistered());
        } else {
            track.trackSearchBarClose();
            setSearchQuery("");
        }
    }, [isOpen]);

    const onInputClick = () => {
        track.trackClickSearchBarInput();
        setIsBotAvailable(onboardingAgentExternalService.isRegistered());
        open();
    };

    const onKeywordClick: OnSearchItemClick = (keyword, state) => {
        track.trackSearchBarResult("keyword", keyword ?? "", state);
    };

    const onWebsiteClick: OnSearchItemClick = (website, state) => {
        track.trackSearchBarResult("website", website ?? "", state);
    };

    const onReportClick: OnSearchItemClick = (reportName, state) => {
        track.trackSearchBarReport(reportName ?? "", state ?? "search_result");
    };

    const onTabClick = (tab: string) => {
        track.trackSearchBarTab(tab);
    };

    const handleLiveSearchQueryChange = useCallback((query: string) => {
        setSearchQuery(query);
    }, []);

    const handleBotStripClick = useCallback(
        (query: string) => {
            track.trackSearchBarAIAssistantClick(query);
            close();
            onboardingAgentExternalService.openWithMessage(query || undefined);
        },
        [close, track],
    );

    const handleTrackUserQuery = useCallback(
        (query: string, tab: string) => {
            track.trackSearchBarUserQuery(query, tab);
        },
        [track],
    );

    return (
        <SC.Container
            $isOpen={isOpen}
            data-automation={searchBarDataAutomation.container}
        >
            <SectionTitle
                title={translate(SEARCH_TITLE)}
                dataAutomation={searchBarDataAutomation.title}
            />

            <SC.StyledSearchInput
                $isOpen={isOpen}
                className="searchInput"
                placeholder={translate(SEARCH_TITLE)}
                onFocus={onInputClick}
            />

            {isOpen ? (
                <div
                    ref={searchRef}
                    data-automation={searchBarDataAutomation.searchAutocomplete}
                >
                    <SC.SearchPanelWrapper>
                        <SearchAutocompleteWithTabs
                            isOpen={isOpen}
                            onClose={close}
                            onKeywordClick={onKeywordClick}
                            onWebsiteClick={onWebsiteClick}
                            onReportClick={onReportClick}
                            onTabClick={onTabClick}
                            onTrackUserQuery={handleTrackUserQuery}
                            tabPanelScrollMaxHeightPx={QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX}
                            tabPanelScrollMinHeightPx={QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX}
                            onLiveSearchQueryChange={handleLiveSearchQueryChange}
                        />
                        {isBotAvailable ? (
                            <QuickSearchBotStrip
                                query={searchQuery}
                                onClick={handleBotStripClick}
                            />
                        ) : null}
                    </SC.SearchPanelWrapper>
                </div>
            ) : null}
        </SC.Container>
    );
};
